/**
 * CryptoStorage - Almacenamiento encriptado en sessionStorage usando AES-GCM.
 *
 * La clave de encriptación se genera en memoria al inicializar y nunca se persiste.
 * Si la pestaña se cierra o recarga, la clave se pierde y los datos cifrados
 * quedan ilegibles (se limpian automáticamente al intentar leerlos).
 */

let encryptionKey = null;

const ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256;
const IV_LENGTH = 12;

async function getKey() {
  if (!encryptionKey) {
    encryptionKey = await crypto.subtle.generateKey(
      { name: ALGORITHM, length: KEY_LENGTH },
      false,
      ['encrypt', 'decrypt']
    );
  }
  return encryptionKey;
}

function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToArrayBuffer(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

export async function setEncrypted(key, data) {
  try {
    const cryptoKey = await getKey();
    const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
    const encoded = new TextEncoder().encode(JSON.stringify(data));

    const ciphertext = await crypto.subtle.encrypt(
      { name: ALGORITHM, iv },
      cryptoKey,
      encoded
    );

    const payload = JSON.stringify({
      iv: arrayBufferToBase64(iv.buffer),
      data: arrayBufferToBase64(ciphertext),
    });

    sessionStorage.setItem(key, payload);
    return true;
  } catch (error) {
    console.warn('[CryptoStorage] Error encrypting:', error);
    return false;
  }
}

export async function getEncrypted(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;

    const { iv, data } = JSON.parse(raw);
    const cryptoKey = await getKey();

    const decrypted = await crypto.subtle.decrypt(
      { name: ALGORITHM, iv: new Uint8Array(base64ToArrayBuffer(iv)) },
      cryptoKey,
      base64ToArrayBuffer(data)
    );

    return JSON.parse(new TextDecoder().decode(decrypted));
  } catch {
    sessionStorage.removeItem(key);
    return null;
  }
}

export function removeEncrypted(key) {
  sessionStorage.removeItem(key);
}

export function hasEncrypted(key) {
  return sessionStorage.getItem(key) !== null;
}
